import { useEffect, useState } from 'react';
import { fetchServiceTokens, fetchUsers } from './api';
import { ServiceTokensTab } from './ServiceTokensTab';
import { UsersTab } from './UsersTab';
import type { ServiceTokenRow, UserRow } from './types';

type Tab = 'users' | 'tokens';

export function UsersPage() {
  const [tab, setTab] = useState<Tab>('users');
  const [users, setUsers] = useState<UserRow[]>([]);
  const [viewIds, setViewIds] = useState<readonly string[]>([]);
  const [tokens, setTokens] = useState<ServiceTokenRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    (async () => {
      try {
        const [u, t] = await Promise.all([fetchUsers(), fetchServiceTokens()]);
        if (cancelled) return;
        setUsers(u.users);
        setViewIds(u.view_ids);
        setTokens(t);
      } catch (x) {
        if (cancelled) return;
        setErr(x instanceof Error ? x.message : 'Fejl ved indlæsning');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div>
      <h1 className="mb-4 text-2xl font-semibold text-primary">Users</h1>

      <div className="mb-4 flex gap-1 border-b border-line-2">
        <TabButton active={tab === 'users'} onClick={() => setTab('users')}>
          Users
        </TabButton>
        <TabButton active={tab === 'tokens'} onClick={() => setTab('tokens')}>
          Service Tokens
        </TabButton>
      </div>

      {loading ? (
        <p className="text-ink-2">Indlæser…</p>
      ) : err ? (
        <p className="text-red-700">{err}</p>
      ) : tab === 'users' ? (
        <UsersTab users={users} viewIds={viewIds} onUsersChange={setUsers} />
      ) : (
        <ServiceTokensTab users={users} tokens={tokens} onTokensChange={setTokens} />
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`border-b-2 px-4 py-2 text-sm ${
        active
          ? 'border-primary font-semibold text-primary'
          : 'border-transparent text-ink-2 hover:text-ink-0'
      }`}
    >
      {children}
    </button>
  );
}
